import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { connect } from 'react-redux';
import { reduxForm, Field } from 'redux-form';
import { toastr } from 'react-redux-toastr';

import {
  addOnlineForm,
  getStorageSetting
} from 'actions';

import CustomerAccountInput, { CustomerAccountInputLayout } from 'components/Common/Inputs/CustomerAccountInput';
import ConfirmModal from 'components/Modals/ConfirmModal';
import OnlineForm from 'components/OnlineForms/OnlineForm';
import withContainerErrorBoundary from 'components/ErrorBoundaries/ContainerErrorBoundary/withContainerErrorBoundary';

class OnlineForms extends Component {
  static propTypes = {
    dispatch: PropTypes.func.isRequired
  };

  constructor(props) {
    super(props);

    const clients = this.filterClientList() || [];
    const clientKey = (clients != null && clients.length === 1) ? clients[0].key : '';

    this.state = {
      clientId: clientKey,
      customerAccount: null,
      formReference: '',
      showImyWarning: null
    };
  }

  onCustomerAccountChange(data) {
    this.setState({ customerAccount: data });
    this.setState({ showImyWarning: null });
  }

  onFormReferenceChange = (e, newValue) => this.setState({ formReference: newValue });

  warnAboutImyStructure = formType => this.setState({ showImyWarning: formType });

  /**
   * Determines if a form type is a CRS form (includes CRS-E, CRS-I, CRS-CP, UCRS variants, RC forms, FATCA/CRS forms)
   */
  // eslint-disable-next-line class-methods-use-this
  isCrsForm = (formType) => {
    if (!formType) return false;
    return formType.includes('crs') || formType.includes('rc') || formType.includes('fatca_crs');
  };

  /**
   * Determines if a form type is a CARF form (includes UCARF variants like UCARFI, UCARFE, UCARFCP)
   */
  // eslint-disable-next-line class-methods-use-this
  isCarfForm = (formType) => {
    if (!formType) return false;
    return formType.includes('ucarf');
  };

  /**
   * Checks if all legal entities are CRS participating
   * All-or-nothing rule: ALL entities must be CRS participating, or the check fails
   */
  // eslint-disable-next-line class-methods-use-this
  areAllEntitiesCrsParticipating = (legalEntities) => {
    if (!legalEntities || legalEntities.length === 0) return false;
    return legalEntities.every(le => le.crsParticipating === true);
  };

  /**
   * Checks if all legal entities are CARF participating
   * All-or-nothing rule: ALL entities must be CARF participating, or the check fails
   */
  // eslint-disable-next-line class-methods-use-this
  areAllEntitiesCarfParticipating = (legalEntities) => {
    if (!legalEntities || legalEntities.length === 0) return false;
    return legalEntities.every(le => le.carfParticipating === true);
  };

  addOnlineForm = (formType) => {
    const { dispatch } = this.props;
    const { clientId, customerAccount, formReference } = this.state;

    if (!customerAccount) {
      toastr.error('', 'Please select a customer or create a new customer.');
      return;
    }
    if (!customerAccount.newCustomer && !customerAccount.customerId) {
      toastr.error('', 'Please provide a valid value for an existing customer or create a new one.');
      return;
    }
    if (!customerAccount.newAccount && (!customerAccount.accounts || customerAccount.accounts.length === 0)) {
      toastr.error('', 'Please provide a valid value for an existing account or create a new one.');
      return;
    }
    if (!customerAccount.legalEntities || customerAccount.legalEntities.length === 0) {
      toastr.error('', 'Please provide a valid value for an existing legal entity.');
      return;
    }

    // All-or-nothing validation: Check eligibility based on form type
    // For CRS forms: ALL selected entities must be CRS participating
    if (this.isCrsForm(formType)) {
      if (!this.areAllEntitiesCrsParticipating(customerAccount.legalEntities)) {
        toastr.error('', 'All entities must be eligible for this form type. Please review the selected legal entities to ensure all are CRS Participating.');
        return;
      }
    }

    // For CARF forms: ALL selected entities must be CARF participating
    if (this.isCarfForm(formType)) {
      if (!this.areAllEntitiesCarfParticipating(customerAccount.legalEntities)) {
        toastr.error('', 'All entities must be eligible for this form type. Please review the selected legal entities to ensure all are CARF Participating.');
        return;
      }
    }

    // NOTE: If/when legal entities can be CRS-only vs CARF-only, add a validation here to prevent mixing regimes.
    // (Currently all validation is handled by the per-form-type eligibility checks above.)

    // FATCA/CRS combined forms have an additional constraint: entities cannot be US Withholding Agents
    const combinedAllowed = customerAccount.legalEntities.filter(le => (le.chapter4Status === 1 || le.chapter4Status === 2)).length === 0;
    if (formType && formType.includes('fatca_crs') && !combinedAllowed) {
      toastr.error('', 'Cannot enter FATCA/CRS Combined forms for legal entities which are US Withholding Agents. Please review the selected legal entities.');
      return;
    }

    if (!clientId) {
      toastr.error('', 'Please provide a valid value for an existing client.');
      return;
    }

    dispatch(addOnlineForm(formType, clientId, customerAccount, formReference));
  };

  // eslint-disable-next-line class-methods-use-this
  filterClientList = () => {
    const clients = getStorageSetting('clients') || [];
    const selectedClient = getStorageSetting('selectedClient');
    let filteredClientList = clients;

    if (selectedClient != null) {
      filteredClientList = [];
      filteredClientList.push(selectedClient);
    }

    return filteredClientList;
  };

  render() {
    const {
      clientId,
      formReference,
      customerAccount,
      showImyWarning
    } = this.state;

    const getParentAccountId = account => account.parentAccountId ?? account.value;
    const accountsPartOfAnImy = customerAccount?.accounts.filter(x => x?.parentAccountId > 0) ?? [];
    const accountsWhereImyTopLevel = customerAccount?.accounts.filter(x => !(x?.parentAccountId) && x?.withholdingStatementId) ?? [];

    return (
      <div className="online-forms">
        <form
          id="onlineForm"
          onSubmit={ values => this.addOnlineForm(values) }
          className="form-horizontal"
        >
          <div className="columns outer-box form-selection mb">
            <div className="form-group col-9 col-sm-12">
              <CustomerAccountInput
                clientId={ clientId || 0 }
                onSelectionChange={ data => this.onCustomerAccountChange(data) }
                layout={ CustomerAccountInputLayout.Horizontal }
              />
            </div>
            <div className="form-group col-3 col-sm-12">
              <div className="form-group col-12">
                <div className="col-5 col-sm-12 text-right">
                  <label htmlFor="formReference" className="form-label">Form Ref.</label>
                </div>
                <div className="col-7 col-sm-12">
                  <Field
                    id="formReference"
                    name="formReference"
                    component="input"
                    className="form-input"
                    value={ formReference }
                    onChange={ (e, newValue) => this.onFormReferenceChange(e, newValue) }
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="relative-container">
            <div className={ `col-12 outer-box ${ accountsPartOfAnImy.length > 0 ? 'disabling-overlay' : '' }` }>
              <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="crse" title="CRS-E Form" /></div>
              <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="crscp" title="CRS-CP Form" /></div>
              <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="crsi_2024" title="CRS-I Form" /></div>
              <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="ucrsi_2025" title="UCRS-I Form" /></div>
              <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="ucrse_2025" title="UCRS-E Form" /></div>
              <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="ucrscp_2025" title="UCRS-CP Form" /></div>
              <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="ucarfi_2026" title="UCARF-I Form" /></div>
              <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="ucarfe_2026" title="UCARF-E Form" /></div>
              <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="ucarfcp_2026" title="UCARF-CP Form" /></div>
              <div className="form"><OnlineForm handleClick={ accountsWhereImyTopLevel.length === 0 ? this.addOnlineForm : this.warnAboutImyStructure } formType="w8bene_2021" title="W-8BEN-E Form" /></div>
              <div className="form"><OnlineForm handleClick={ accountsWhereImyTopLevel.length === 0 ? this.addOnlineForm : this.warnAboutImyStructure } formType="w8ben_2021" title="W-8BEN Form" /></div>
              <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="w8imy_2021" title="W-8IMY Form" /></div>
              <div className="form"><OnlineForm handleClick={ accountsWhereImyTopLevel.length === 0 ? this.addOnlineForm : this.warnAboutImyStructure } formType="w9_2024" title="W-9 Form" /></div>
              <div className="form"><OnlineForm handleClick={ accountsWhereImyTopLevel.length === 0 ? this.addOnlineForm : this.warnAboutImyStructure } formType="w8eci_2021" title="W-8ECI Form" /></div>
              <div className="form"><OnlineForm handleClick={ accountsWhereImyTopLevel.length === 0 ? this.addOnlineForm : this.warnAboutImyStructure } formType="w8exp_2023" title="W-8EXP Form" /></div>
              <div className="form"><OnlineForm handleClick={ accountsWhereImyTopLevel.length === 0 ? this.addOnlineForm : this.warnAboutImyStructure } formType="fatca_crsi_2023" title="FATCA/CRS-I Form" /></div>
              <div className="form"><OnlineForm handleClick={ accountsWhereImyTopLevel.length === 0 ? this.addOnlineForm : this.warnAboutImyStructure } formType="fatca_crse_2023" title="FATCA/CRS-E Form" /></div>
              <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="rc519_2025" title="RC-519 Form" /></div>
              <div className="form"><OnlineForm handleClick={ this.addOnlineForm } formType="rc518_2025" title="RC-518 Form" /></div>
            </div>
            { accountsPartOfAnImy?.length > 0
            && <div className="error-box simple-modal">
              <div className="modal-container">
                <div className="modal-header">
                  <div className="modal-title">Unable To Add Form To Account(s)</div>
                </div>
                <div className="modal-body">
                  <div className="content">
                    {
                      accountsPartOfAnImy.map(a => <p> {a.label} is linked to an IMY Structure. Any new form for this account must be uploaded from the <a href={ `/whd-statement/${ getParentAccountId(a) }` }><u>IMY Structure page.</u></a></p>)
                    }
                  </div>
                </div>
              </div>
            </div> }
            <ConfirmModal
              isShown={ !!showImyWarning }
              title="Intermediary Structure Warning"
              message={ <div>
                <p>This will add a form to the top level Intermediary Account of a IMY Structure.</p>
                <p>Uploading a W8/W9 form (other than a W-8IMY) will lead to changes on the account that will impact the IMY Structure.</p>
                <p>Are you sure you wish to continue?</p>
              </div> }
              confirmTitle="Continue"
              onConfirm={ () => this.addOnlineForm(showImyWarning) }
              onCancel={ () => this.setState({ showImyWarning: null }) }
            />
          </div>
        </form>
      </div>
    );
  }
}

const OnlineReduxForm = reduxForm({
  form: 'onlineForm',
})(OnlineForms);

function mapStateToProps(state) {
  const { forms: {
    isAddingOnlineForm
  } } = state;
  return {
    isAddingOnlineForm
  };
}

const mapDispatchToProps = dispatch => ({ dispatch });

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withContainerErrorBoundary(OnlineReduxForm, 'OnlineForms'));
